import _ from 'lodash'
import FetchUpdatedAt from "utils/fetch/FetchUpdatedAt";
import store from 'services/redux/store'
import { dataConstants } from 'services/redux/data/constants'
import { MODES } from 'utils/constants/app-modes'
import HelperDate, { dateFormat } from 'utils/helper-date'

const CacheByUpdatedAt = {
	
	defaultDatetime: '0000-00-00 00:00:00',
	
	isInitialized: false,
	updated_at: null,
	tables_updated_at: {},
	tables_updated: {},
	tables_on_expire: {},
	tables: [],
	table_updated_at_ages: {},
	backgroundReloadTimeout: null,
	backgroundReloadTimeoutMs: 30 * 1000,
	forceReload: false,
	inFlightRequest: null,
	backgroundFailures: 0,
	maxRetryDelayMs: 5 * 60 * 1000,
	
	setForceReload: ( forceReload = true ) => {
		CacheByUpdatedAt.forceReload = forceReload;
	},
	
	/*
		Function registers table(s) and their max age which is used later when sniffing the cache
	 */
	register: ( { tables = null , updated_at_max_age = 30, onExpire = null } ) => {
		tables.forEach( ( table ) => {
			// create all the basic data
			if( _.includes( CacheByUpdatedAt.tables, table ) === false ) {
				CacheByUpdatedAt.tables.push( table ); 										// push the list of tables we need to check on the server
				CacheByUpdatedAt.table_updated_at_ages[ table ] = 99999;					// set the age to a fictional nr, will be overwritten after this creation part
				CacheByUpdatedAt.tables_updated_at[ table ] = CacheByUpdatedAt.defaultDatetime;		// set the update at to 'creation of time' to ensure that first pull/check is
				CacheByUpdatedAt.tables_updated[ table ] = false;							// set the updated at of a table to false (will be updated in first run anyway)

				if( onExpire !== null ) {
					CacheByUpdatedAt.tables_on_expire[ table ] = onExpire;
				}
			}

			// if the new updated_at_max_age is lower overwrite the value
			if( CacheByUpdatedAt.table_updated_at_ages[ table ] > updated_at_max_age ) {
				CacheByUpdatedAt.table_updated_at_ages[ table ] = updated_at_max_age;
			}
			
			if( CacheByUpdatedAt.backgroundReloadTimeoutMs > updated_at_max_age * 1000 ) {
				CacheByUpdatedAt.backgroundReloadTimeoutMs = updated_at_max_age * 1000;
			}
		});
	},
	
	initialized: ( callback ) => {
		CacheByUpdatedAt.isInitialized = true;
		CacheByUpdatedAt.backgroundReload( callback, true );
	},
	
	getTableUpdatedAt: ( table ) => {
		return _.get( CacheByUpdatedAt.tables_updated_at, table, CacheByUpdatedAt.defaultDatetime );
	},

	/*
		Sniff the database goodness and smell if it's fresh or expired data
	 */
	sniff: async ( { tables, onExpired = null, onFresh = null } ) => {
		// Ensure that tables is always an array
		if( Array.isArray( tables ) === false ) {
			tables = [ tables ];
		}
		
		// get the lowest cache time of all the queried tables
		let lowestUpdatedAtMaxAge = 99999;
		tables.forEach( ( table ) => {
			if( table in CacheByUpdatedAt.table_updated_at_ages && CacheByUpdatedAt.table_updated_at_ages[ table ] < lowestUpdatedAtMaxAge ) {
				lowestUpdatedAtMaxAge = CacheByUpdatedAt.table_updated_at_ages[ table ];
			}
		});

		// convert max age to date/time
		const expireTime = HelperDate.date().subtract( lowestUpdatedAtMaxAge, 'seconds').format( dateFormat.DATE_TIME );

		// if updated at is null or old we need to query the database again
		if( CacheByUpdatedAt.updated_at === null || CacheByUpdatedAt.updated_at <= expireTime ) {
			return CacheByUpdatedAt.reloadUpdatedAt( { tables, onExpired, onFresh  } );
		}
		
		return CacheByUpdatedAt.validateUpdatedAts( { tables, onExpired, onFresh } );
	},

	/*
		Register that we updated a table, this should prevent async
	 */
	registerAsUpdates: ( { tables = null, updated_at = null } ) => {
		if( tables !== null ) {
			tables.forEach( ( table ) => {
				CacheByUpdatedAt.tables_updated[ table ] = false;
				CacheByUpdatedAt.tables_updated_at[ table ] = updated_at ?? CacheByUpdatedAt.getTableUpdatedAt( table );
			});
		}
	},
	/*
		Fetch the updated at values of all tables from the server
	 */
	requestUpdatedAt: async ( tables = CacheByUpdatedAt.tables ) => {
		if( !CacheByUpdatedAt.inFlightRequest ) {
			const requestedTables = _.uniq( [ ...CacheByUpdatedAt.tables, ...tables ] );
			const promise = new Promise( resolve => {
				try {
					FetchUpdatedAt.fetch({
						tables: requestedTables,
						callbackSuccess: ({ updated_ats = [] }) => {
							return resolve({
								status: 'success',
								updated_ats
							});
						},
						callbackError: () => {
							return resolve({
								status: 'error'
							});
						},
						callbackCancelled: () => {
							return resolve({
								status: 'cancelled'
							});
						},
					});
				} catch {
					resolve({ status: 'error' });
				}
			}).then( result => {
				if( result.status === 'success' ) {
					CacheByUpdatedAt.setUpdatedAt( result );
					CacheByUpdatedAt.updated_at = HelperDate.date().format( dateFormat.DATE_TIME );
					result.tableChanges = { ...CacheByUpdatedAt.tables_updated };
				}
				return result;
			}).finally( () => {
				CacheByUpdatedAt.inFlightRequest = null;
			} );
			CacheByUpdatedAt.inFlightRequest = { tables: requestedTables, promise };
		}
		const request = CacheByUpdatedAt.inFlightRequest;
		const result = await request.promise;
		// Tables registered during this request need a subsequent check.
		if( result.status === 'success' && tables.some( table => {
			return !request.tables.includes(table);
		} ) ) {
			return CacheByUpdatedAt.requestUpdatedAt( tables );
		}
		return result;
	},

	reloadUpdatedAt: async ( { tables = null, onExpired = null, onFresh = null } ) => {
		const result = CacheByUpdatedAt.isInitialized
			? await CacheByUpdatedAt.requestUpdatedAt( tables ?? CacheByUpdatedAt.tables )
			: { status: 'error' };
		if( result.status !== 'success' ) {
			if( _.isFunction( onExpired ) ) onExpired();
			return false;
		}
		return CacheByUpdatedAt.validateUpdatedAts({ tables, onExpired, onFresh, tableChanges: result.tableChanges });
	},

	/*
		Process the updated at values from all the tables received from the server
	 */
	receivedUpdatedAt: ( { tables = null, updated_ats = null, onExpired = null, onFresh = null  } ) => {
		// register all database changes
		CacheByUpdatedAt.setUpdatedAt( { updated_ats: updated_ats } );

		// register that we updated all the updated ats
		CacheByUpdatedAt.updated_at = HelperDate.date().format( dateFormat.DATE_TIME );

		// run the validation of data
		return CacheByUpdatedAt.validateUpdatedAts( { tables, onExpired, onFresh } );
	},

	setUpdatedAt: ( { updated_ats = [] } ) => {
		_.forEach( updated_ats, ( table ) => {
			CacheByUpdatedAt.tables_updated[ table.table ] = CacheByUpdatedAt.tables_updated[ table.table ] || ( table.updated_at > CacheByUpdatedAt.getTableUpdatedAt( table.table ) );
			CacheByUpdatedAt.tables_updated_at[ table.table ] = table.updated_at;
		});
	},

	writeToConsole: () => {
		CacheByUpdatedAt.log( { prefix: '::writeToConsole', message: CacheByUpdatedAt.tables_updated_at } );
	},

	/*
		Validate all the updated ats we've received/know with the tables that are required by this table
	 */
	validateUpdatedAts: ( { tables = null, onExpired = null, onFresh = null, tableChanges = CacheByUpdatedAt.tables_updated } ) => {
		// Check if tables where registered as updated!
		let isFresh = true;
		
		_.forEach( tables, ( table ) => {
			// Not fresh when table is updated or the table updated at is zero
			if( tableChanges[ table ] || CacheByUpdatedAt.getTableUpdatedAt( table ) === CacheByUpdatedAt.defaultDatetime ) {
				isFresh = false;
			}
		});
		
		if( CacheByUpdatedAt.forceReload ) {
			isFresh = false;
		}

		// Fresh is UP-TO-DATE
		if( isFresh ) {
			if( _.isFunction( onFresh ) ) {
				onFresh();
			}
			return true;
		}

		if( _.isFunction( onExpired ) ) {
			onExpired();
		}
		return false;
	},

	createBackgroundReloadTimeout: ( delay = CacheByUpdatedAt.backgroundReloadTimeoutMs, callback, firstCall = false ) => {
		if( CacheByUpdatedAt.backgroundReloadTimeout === null ) {
			CacheByUpdatedAt.backgroundReloadTimeout = setTimeout(
				() => {
					return CacheByUpdatedAt.backgroundReload(callback, firstCall);
				}, delay
			);
		}
	},

	backgroundReload: async ( callback, firstCall = false ) => {
		const isInManagement = store.getState().data[ dataConstants.MODE ] === MODES.MANAGEMENT;
		clearTimeout( CacheByUpdatedAt.backgroundReloadTimeout );
		CacheByUpdatedAt.backgroundReloadTimeout = null;
		if( isInManagement && !firstCall ) {
			CacheByUpdatedAt.createBackgroundReloadTimeout();
			return;
		}

		const result = await CacheByUpdatedAt.requestUpdatedAt();
		if( result.status === 'error' ) {
			CacheByUpdatedAt.backgroundFailures += 1;
			const delay = Math.min( CacheByUpdatedAt.maxRetryDelayMs,
				Math.max( 1000, CacheByUpdatedAt.backgroundReloadTimeoutMs ) * 2 ** ( CacheByUpdatedAt.backgroundFailures - 1 ) );
			CacheByUpdatedAt.createBackgroundReloadTimeout( delay, callback, firstCall );
			return;
		}
		if( result.status === 'cancelled' ) {
			CacheByUpdatedAt.createBackgroundReloadTimeout( CacheByUpdatedAt.backgroundReloadTimeoutMs, callback, firstCall );
			return;
		}

		CacheByUpdatedAt.backgroundFailures = 0;
		try {
			if( _.isFunction( callback ) ) {
				callback();
			} else {
				result.updated_ats.forEach( table => {
					if( CacheByUpdatedAt.tables_updated[ table.table ] ) {
						CacheByUpdatedAt.tables_on_expire[ table.table ]?.();
					}
				});
			}
		} finally {
			CacheByUpdatedAt.createBackgroundReloadTimeout();
		}
	},

	log( { prefix, message } ) {
		console.prefix( `CacheByUpdatedAt ${prefix}`, message );
	},
}

export default CacheByUpdatedAt
